<template>
  <full-screen :title="t('printers.title')" @close="router.back()">
    <div class="p-4 max-w-6xl m-auto">
      <div class="mb-4 flex justify-end">
        <l-button @click="showDiscovery = !showDiscovery">
          {{ t('printers.discovery') }}
        </l-button>
      </div>
      <l-table :columns="columns" :data="data">
        <template #cell(status)="cell">
          <l-table-cell v-bind="cell">
            <printer-status :queue="queues[cell.row.id]" />
          </l-table-cell>
        </template>

        <template #cell(mode)="cell">
          <div class="flex items-center gap-2 flex-wrap p-2">
            <template v-if="cell.row.isBillPrinter">
              <l-status type="secondary" status="inactive" :show-icon="false" size="small">
                {{ t('printers.bill-printer') }}
              </l-status>
            </template>
            <template v-if="cell.row.isKitchenPrinter">
              <l-status type="secondary" status="inactive" :show-icon="false" size="small">
                {{ t('printers.kitchen-printer') }}
              </l-status>
            </template>
            <template v-if="cell.row.isDeliveryPrinter">
              <l-status type="secondary" status="inactive" :show-icon="false" size="small">
                {{ t('printers.delivery-printer') }}
              </l-status>
            </template>
            <template v-if="cell.row.isTillPrinter">
              <l-status type="secondary" status="inactive" :show-icon="false" size="small">
                {{ t('printers.till-printer') }}
              </l-status>
            </template>
            <l-status
              type="secondary"
              status="inactive"
              :show-icon="false"
              size="small"
              v-for="floorplan in cell.row.floorplans"
              :key="floorplan.id"
            >
              {{ floorplan.name }}
            </l-status>
          </div>
        </template>

        <template #cell(actions)="cell">
          <l-table-cell v-bind="cell">
            <l-button
              size="small"
              type="text"
              @click="printTest(cell.row)"
              v-if="
                (cell.row.type !== 'Usb' && cell.row.type !== 'KDS') ||
                (cell.row.type === 'Usb' && isElectron) ||
                (cell.row.type === 'UsbAndroid' && isAndroid)
              "
            >
              {{ t('printers.print-test') }}
            </l-button>
          </l-table-cell>
        </template>
      </l-table>
    </div>
    <printer-discovery
      v-if="showDiscovery"
      @new-printer="handleNewPrinter"
      @close="showDiscovery = false"
    />
    <printer-editor v-if="showEditor" :printer="discoveredPrinter" @close="showEditor = false" />
  </full-screen>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LButton, LStatus, LTable, LTableCell } from '@last/core-ui/paprika'

import FullScreen from '@/components/FullScreen.vue'
import Printer, { queues } from '@/integrations/printer/printer'
import { useConfigStore } from '@/store/config'
import { useFloorplanStore } from '@/store/floorplan'
import { useTillStore } from '@/store/till'
import { printerTest as ticketPrinterTest } from '@/tickets/printerTest'
import type { Printer as PrinterType } from '@/types/config'
import { isElectron } from '@/utils/isElectron'
import { isAndroid } from '@/utils/isMobile'

import PrinterDiscovery from './PrinterDiscovery.vue'
import PrinterEditor from './PrinterEditor.vue'
import PrinterStatus from './PrinterStatus.vue'

const configStore = useConfigStore()
const floorplanStore = useFloorplanStore()
const till = useTillStore()
const router = useRouter()

const { printers, config } = storeToRefs(configStore)
const { floorplans } = storeToRefs(floorplanStore)
const { selectedCashTill } = storeToRefs(till)

const showDiscovery = ref(false)
const showEditor = ref(false)
const discoveredPrinter = ref<{
  type: 'EscPos' | 'Usb' | 'UsbAndroid'
  ip?: string
}>()

const columns = computed(() => [
  {
    key: 'name',
    header: t('printers.name')
  },
  {
    key: 'type',
    header: t('printers.type')
  },
  {
    key: 'ip',
    header: t('printers.ip')
  },
  {
    key: 'status',
    header: t('printers.status'),
    cell: {
      ignoreKey: true
    }
  },
  {
    key: 'mode',
    header: t('printers.mode'),
    cell: {
      ignoreKey: true
    }
  },
  {
    key: 'actions',
    header: '',
    cell: {
      ignoreKey: true
    }
  }
])

const { t } = useI18n()

const data = computed(() => {
  return Object.values(printers.value).map(printer => {
    return {
      id: printer.id,
      name: printer.name,
      type: printer.type,
      ip: printer.ip || '-',
      isBillPrinter: config.value.defaultBillPrinter === printer.id,
      isKitchenPrinter: config.value.defaultKitchenPrinter === printer.id,
      isDeliveryPrinter:
        config.value.deliveryPrinterId === printer.id || config.value.deliveryTillId === printer.id,
      isTillPrinter: selectedCashTill.value?.printerId === printer.id,
      floorplans: Object.values(floorplans.value).filter(
        floorplan => floorplan.controlPrinter === printer.id
      )
    }
  })
})

function handleNewPrinter(printer: {
  name?: string
  type: 'EscPos' | 'Usb' | 'UsbAndroid'
  ip?: string
}) {
  discoveredPrinter.value = printer
  showDiscovery.value = false
  showEditor.value = true
}

async function printTest(printer: PrinterType) {
  const ticket = ticketPrinterTest(printer)
  const imageData = ticket.toDataURL('image/png')

  Printer.printImage(printer.id, imageData)
}
</script>
